import React, { useMemo } from "react";

import {
  faEye,
  faPencil,
  faPaperPlane,
} from "@fortawesome/pro-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";

import { BillingDocumentAction } from "actions";

import { MultiButton, SlimButton } from "components/Button";

import {
  BillingDocumentReportJobStatus,
  BillingDocumentType,
} from "constants/billingDocuments";
import { ModalTypes } from "constants/navigation";

import {
  buildPaymentAdviceButtons,
  getPaymentAdviceDirection,
  groupPayments,
} from "lib/billingDocumentPaymentAdvice";
import { getReportJobUrl, openModalLink } from "lib/navigation";

import { selectCanSendToXeroByBillingDocumentIdLookup } from "selectors";

export const BillingDocumentActionRenderer = ({ data, context }) => {
  const { billingDocument = {}, payments = [], livestockSales } = data || {};

  const reportJobId = billingDocument.reportJobs?.find(
    rj => rj.documentStatus === BillingDocumentReportJobStatus.COMMITTED,
  )?.id;

  const openPreview = reportJobId
    ? () => window.open(getReportJobUrl(reportJobId, context.userRoleSlug))
    : undefined;

  const saleId = livestockSales?.[0]?.id;

  const openEditModal = saleId
    ? () =>
        openModalLink(ModalTypes.EditBillingDocument, {
          id: billingDocument.id,
          saleId,
        })
    : undefined;

  const dispatch = useDispatch();

  const sendToXero = () =>
    dispatch(BillingDocumentAction.exportXeroInvoiceApi([billingDocument.id]));

  const canSendToXero = useSelector(
    selectCanSendToXeroByBillingDocumentIdLookup,
  )[billingDocument.id];

  const direction = getPaymentAdviceDirection(billingDocument.type);

  const paymentGroups = useMemo(() => groupPayments(payments), [payments]);

  const emailBusinessId =
    billingDocument.type === BillingDocumentType.RCTI
      ? billingDocument.recipientBusinessId
      : billingDocument.issuerBusinessId;

  const paymentAdviceButtons = buildPaymentAdviceButtons({
    paymentGroups,
    direction,
    userRoleSlug: context.userRoleSlug,
    emailBusinessId,
    saleId,
  });

  const buttons = [
    {
      dataTour: "viewBillingDocument",
      default: true,
      icon: faEye,
      isDisabled: !reportJobId,
      onClick: openPreview,
      title: "View",
      tooltip: "View the Billing Document",
    },
    openEditModal && {
      dataTour: "editBillingDocument",
      icon: faPencil,
      title: "Edit",
      onClick: openEditModal,
    },
    canSendToXero && {
      dataTour: "sendToXero",
      icon: faPaperPlane,
      title: "Send To Xero",
      onClick: sendToXero,
    },
    ...paymentAdviceButtons,
  ].filter(Boolean);

  return <MultiButton buttons={buttons} ButtonComponent={SlimButton} />;
};
